import type { Rng } from './prng';
import type { SettingId } from './setting';

/** Generated Italian-sounding circuit names, e.g. "Circuito di Valverde". */

interface Root {
  full: string;
  elided: string;
  feminine: boolean;
}

const ROOTS: Root[] = [
  { full: 'Val', elided: 'Val', feminine: false },
  { full: 'Monte', elided: 'Mont', feminine: false },
  { full: 'Castel', elided: 'Castel', feminine: false },
  { full: 'Borgo', elided: 'Borg', feminine: false },
  { full: 'Rocca', elided: 'Rocc', feminine: true },
  { full: 'Colle', elided: 'Coll', feminine: false },
  { full: 'Poggio', elided: 'Pogg', feminine: false },
  { full: 'Serra', elided: 'Serr', feminine: true },
  { full: 'Villa', elided: 'Vill', feminine: true },
  { full: 'Torre', elided: 'Torr', feminine: true },
  { full: 'Fonte', elided: 'Font', feminine: true },
  { full: 'Prato', elided: 'Prat', feminine: false },
  { full: 'Pieve', elided: 'Piev', feminine: true },
  { full: 'Campo', elided: 'Camp', feminine: false },
  { full: 'Poggi', elided: 'Pogg', feminine: false },
];

/** Adjectives agree with the root's gender: [masculine, feminine]. */
const ADJECTIVES: [string, string][] = [
  ['verde', 'verde'],
  ['rosso', 'rossa'],
  ['bianco', 'bianca'],
  ['lungo', 'lunga'],
  ['chiaro', 'chiara'],
  ['alto', 'alta'],
  ['nero', 'nera'],
  ['nuovo', 'nuova'],
  ['franco', 'franca'],
  ['bello', 'bella'],
  ['sereno', 'serena'],
  ['fiorito', 'fiorita'],
  ['antico', 'antica'],
  ['secco', 'secca'],
  ['dorato', 'dorata'],
];

const NOUNS = ['fiore', 'sole', 'vento', 'luce', 'marina', 'piano', 'oliva', 'pino', 'forte', 'mare', 'rosa', 'grano'];

const SAINTS = ['San Donato', 'San Quirico', 'San Vitale', 'Santa Fiora', "Sant'Agata", "Sant'Elia", 'San Lorenzo', 'Santa Maura'];

const PREFIXES = ['Circuito di', 'Circuito di', 'Circuito di', 'Autodromo di', 'Pista di', 'Anello di'];

const VOWELS = 'aeiou';

interface Vocab {
  roots: Root[];
  adjectives: [string, string][];
  nouns: string[];
  saints: string[];
}

const r = (full: string, elided: string, feminine = false): Root => ({ full, elided, feminine });

/** Each setting's own place-names (setting.ts): Tuscany's are the lists above, as they always were. */
const VOCAB: Record<SettingId, Vocab> = {
  tuscany: { roots: ROOTS, adjectives: ADJECTIVES, nouns: NOUNS, saints: SAINTS },
  // The Riviera: harbours, points and coves.
  liguria: {
    roots: [r('Porto', 'Port'), r('Riva', 'Riv', true), r('Punta', 'Punt', true), r('Cala', 'Cal', true), r('Marina', 'Marin', true), r('Capo', 'Cap'), r('Monte', 'Mont'), r('Borgo', 'Borg')],
    adjectives: [
      ['azzurro', 'azzurra'],
      ['chiaro', 'chiara'],
      ['verde', 'verde'],
      ['fiorito', 'fiorita'],
      ['antico', 'antica'],
      ['bello', 'bella'],
      ['lungo', 'lunga'],
      ['nuovo', 'nuova'],
    ],
    nouns: ['mare', 'vento', 'sole', 'scoglio', 'pino', 'fiore', 'luce', 'rosa', 'sale'],
    saints: ['San Fruttuoso', 'Santa Margherita', 'San Rocco', 'San Michele', "Sant'Ambrogio"],
  },
  // The mountains: passes, peaks, high pastures.
  dolomites: {
    roots: [r('Passo', 'Pass'), r('Val', 'Val'), r('Pian', 'Pian'), r('Col', 'Col'), r('Cima', 'Cim', true), r('Malga', 'Malg', true), r('Ponte', 'Pont'), r('Selva', 'Selv', true)],
    adjectives: [
      ['alto', 'alta'],
      ['bianco', 'bianca'],
      ['chiaro', 'chiara'],
      ['verde', 'verde'],
      ['nero', 'nera'],
      ['lungo', 'lunga'],
      ['fiorito', 'fiorita'],
      ['freddo', 'fredda'],
    ],
    nouns: ['neve', 'larice', 'sasso', 'fiore', 'vento', 'sole', 'lago', 'bosco'],
    saints: ['San Candido', 'San Vigilio', 'Santa Cristina', 'San Cassiano', 'Santa Maddalena'],
  },
  // The island: capes, castles, wheat and lava.
  sicily: {
    roots: [r('Monte', 'Mont'), r('Castel', 'Castel'), r('Piano', 'Pian'), r('Capo', 'Cap'), r('Cala', 'Cal', true), r('Torre', 'Torr', true), r('Rocca', 'Rocc', true), r('Borgo', 'Borg')],
    adjectives: [
      ['rosso', 'rossa'],
      ['dorato', 'dorata'],
      ['secco', 'secca'],
      ['antico', 'antica'],
      ['bianco', 'bianca'],
      ['sereno', 'serena'],
      ['alto', 'alta'],
    ],
    nouns: ['sole', 'grano', 'oliva', 'zagara', 'mare', 'fico', 'lava', 'vento'],
    saints: ["Sant'Agata", 'Santa Rosalia', 'San Vito', 'Santa Lucia', 'San Calogero'],
  },
};

function place(rng: Rng, v: Vocab): string {
  if (rng.chance(0.12)) return rng.pick(v.saints);
  const root = rng.pick(v.roots);
  const tail = rng.chance(0.65) ? rng.pick(v.adjectives)[root.feminine ? 1 : 0] : rng.pick(v.nouns);
  const stem = VOWELS.includes(tail[0]) ? root.elided : root.full;
  return stem + tail;
}

export const MAX_NAME_LENGTH = 24;

/** The day's circuit name, from its setting's place-names (Tuscany's unless told otherwise). */
export function generateCircuitName(rng: Rng, setting: SettingId = 'tuscany'): string {
  for (let attempt = 0; attempt < 20; attempt++) {
    const name = `${rng.pick(PREFIXES)} ${place(rng, VOCAB[setting])}`;
    if (name.length <= MAX_NAME_LENGTH) return name;
  }
  return 'Circuito di Valverde';
}

/** "Anello di San Fruttuoso" → "San Fruttuoso": how a headline (or a Discord message) says it. */
export function shortCircuit(name: string): string {
  return name.replace(/^(Circuito|Autodromo|Pista|Anello) di /i, '');
}
